<template>
  <div>
    <validated-input-box
      id="loginFieldResetPin"
      ref="loginField"
      :validation-reg-exp="numberRegex"
      :validation-error-message="$i18n.t('COMMON.ERROR_MESSAGES.LOGIN_ERROR')"
      :label="this.$i18n.t('PASSWORD_RESET.LOGIN_NUMBER')"
      placeholder="6789123456789"
      name="loginField"
    />
    <div 
      class="spacing"
      :class="{'link-disabled': !frcResponse}"
    >
      <a
        id="requestCodeButton"
        class="button button--outline button--black button--big"
        :class="{'link-disabled': !frcResponse}"
        :aria-disabled="!frcResponse"
        @click.prevent="requestPin"
      >
        {{ this.$i18n.t('PASSWORD_RESET.REQUEST_PIN') }}
      </a>
    </div>
    <friendly-captcha-field
      ref="captcha"
      v-model="frcResponse"
    />
  </div>
</template>

<script>
import Regex from '@/constants/regex.json';
import FriendlyCaptchaField from '@/components/common/FriendlyCaptchaField.vue';

const RESP_VALIDATION_ERROR = 1;
const RESP_MSISDN_NOT_PREPAID = 2;
const RESP_PASSWORD_INCORRECT = 3;
const RESP_TEMPORARILY_LOCKED = 4;
const RESP_LOCKED = 5;
const RESP_NOT_YET_REGISTERED = 6;
const RESP_PIN_TIME_PROTECTION = 7;
const RESP_PIN_INCORRECT = 8;
const RESP_PIN_EXPIRED = 9;
const RESP_PIN_LOCKED_TEMPORARILY = 10;
const RESP_PIN_LOCKED = 11;
const RESP_OTHER_ERROR = 9999;

export default {
  name: 'PasswordResetPin',
  components: { FriendlyCaptchaField },
  data: function() {
    return {
      inProgress: false,
      frcResponse: null,
    };
  },
  computed: {
    numberRegex: function() {
      return Regex.NUMBER;
    },
  },
  methods: {
    getLoginField: function() {
      return this.$refs.loginField;
    },
    requestPin: function() {
      if (!this.inProgress && this.$refs.loginField.isValidBeforeSubmit() && this.$refs.captcha.isValid()) {
        this.inProgress = true;

        let pinRequest = {
          msisdn: this.$refs.loginField.getFieldValue(),
        }

        // for successful call we just go on, no info, customer gets sms
        this.$http.post('api/protected/passwordResetPin', pinRequest, {
          headers: {
            'friendly-captcha-version': 'v2',
            'friendly-captcha-response': this.frcResponse
          }
        })
          .then( () => {
            this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Success', value: 0, noninteraction: false });
            this.$store.commit("setSuccessMessage", this.$i18n.t('PASSWORD_RESET.PIN_SENT'));
          })
          .catch(callData => {
            this.processErrorCallResponse(callData);
          })
          .finally(() => {
            this.inProgress = false;
            this.$refs.captcha.reset();
          })
      }
    },
    processErrorCallResponse: function(callData) {
      if(callData.response === undefined || callData.response.data === undefined) {
        this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - no data', value: 0, noninteraction: false });
        this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.TECHNICAL_ISSUE'));
        return;
      }

      let responseCode = callData.response.data.responseCode;

      switch(responseCode) {
        case RESP_VALIDATION_ERROR:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.VALIDATION_ERROR'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - validation error', value: 0, noninteraction: false });
          break;
        case RESP_MSISDN_NOT_PREPAID:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.MSISDN_NOT_ACTIVE_PREPAID'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - not active prepaid', value: 0, noninteraction: false });
          break;
        case RESP_TEMPORARILY_LOCKED:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.LOGIN_TEMPORARILY_LOCKED'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - temporarily locked', value: 0, noninteraction: false });
          break;
        case RESP_LOCKED:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.LOGIN_LOCKED'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - locked', value: 0, noninteraction: false });
          break;
        case RESP_NOT_YET_REGISTERED:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.SUBSCRIPTION_NOT_YET_REGISTERED'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - not registered', value: 0, noninteraction: false });
          break;
        case RESP_PIN_TIME_PROTECTION:
          this.$store.commit("setErrorMessage", this.$i18n.t('PASSWORD_RESET.ERROR_MESSAGES.PIN_TIME_PROTECTION'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - PIN time protection', value: 0, noninteraction: false });
          break;
        case RESP_PIN_INCORRECT:
          this.$store.commit("setErrorMessage", this.$i18n.t('PASSWORD_RESET.ERROR_MESSAGES.PIN_INCORRECT'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - PIN incorrect', value: 0, noninteraction: false });
          break;
        case RESP_PIN_EXPIRED:
          this.$store.commit("setErrorMessage", this.$i18n.t('PASSWORD_RESET.ERROR_MESSAGES.PIN_EXPIRED'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - PIN expired', value: 0, noninteraction: false });
          break;
        case RESP_PIN_LOCKED_TEMPORARILY:
          this.$store.commit("setErrorMessage", this.$i18n.t('PASSWORD_RESET.ERROR_MESSAGES.PIN_TEMPORARILY_LOCKED'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - PIN temporarily locked', value: 0, noninteraction: false });
          break;
        case RESP_PIN_LOCKED:
          this.$store.commit("setErrorMessage", this.$i18n.t('PASSWORD_RESET.ERROR_MESSAGES.PIN_LOCKED'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - PIN locked', value: 0, noninteraction: false });
          break;
        case RESP_OTHER_ERROR:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.TECHNICAL_ISSUE'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - technical issue', value: 0, noninteraction: false });
          break;
        default:
          this.$store.commit("setErrorMessage", this.$i18n.t('COMMON.ERROR_MESSAGES.TECHNICAL_ISSUE'));
          this.$gtm.trackEvent({ category: 'Drei TV Streamingkarte',action: 'Password reset PIN',label: 'Error - '+responseCode, value: 0, noninteraction: false });
      }
    },
  }
};
</script>

<style lang="scss" src="@/components/loading/loading.scss">
</style>
<style lang="scss" src="./password-reset-pin.scss">
</style>
